import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Stack,
  useTheme,
  Tooltip,
  Checkbox
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EventIcon from '@mui/icons-material/Event';
import ImageIcon from "@mui/icons-material/Image";
import CreditLimitRequest from "./CreditLimitRequest";
import CollectionRequest from "./CollectionRequest";
import OrderTakingRequest from "./OrderTakingRequest";
import OrderBypassRequest from "./OrderBypassRequest";
import { Request, RequestType } from "./types";
import { handler } from "ace-builds-internal/keyboard/textarea";
import { ConfirmationPopUp } from "../../components/popup/confirmationPopUp";
import { getLabelMap, defaultLabelMap, formatAmount } from "./manageRequestsService";
import { openPopup } from "../../stateManagement/actions/popupActions";


const RequestCard: React.FC<Request> = ({
  id,
  type,
  storeName,
  storeId,
  salesRepId,
  currentCreditLimit,
  availCreditLimit,
  invoiceNumber,
  orderDate,
  data,
  onDataChange,
  onApprove,
  onReject,
  checked,
  onChange,
  status,
  labelMap,
  outletType
}) => {
  const theme = useTheme();
  const [confirmationPopUp, setConfirmationPopUp] = useState(false);
  const [popUpMessage, setPopUpMessage] = useState("");
  // const [labelMap, setLabelMap] = useState(defaultLabelMap);
  const popUpSuccessMethod = useRef<() => void>(() => { });

  // useEffect(() => {
  //   (async () => {
  //     try {
  //       const map = await getLabelMap();
  //       setLabelMap(map);
  //     } catch (err) {
  //       console.warn("Using default label map due to error:", err);
  //       setLabelMap(defaultLabelMap);
  //     }
  //   })();
  // }, []);

  const renderContent = () => {
    switch (type) {
      case "credit-limit": return <CreditLimitRequest key={data} data={data} onDataChange={onDataChange} status={status} outletType={outletType} />;
      case "collection": return <CollectionRequest key={data} data={data} onDataChange={onDataChange} status={status} />;
      case "order-taking": return <OrderTakingRequest key={data} data={data} />;
      case "order-bypass": return <OrderBypassRequest key={data} data={data} onDataChange={onDataChange} status={status} />;
      default: return null;
    }
  };

  const handleApproveClick = () => {
    if (type === "credit-limit") {
      const amount = data?.amount ?? "0";
      if (amount > Number(currentCreditLimit.replace(/,/g, ""))) {
        openPopup("Alert", `New Limit must be less than SAR ${formatAmount(currentCreditLimit)}`);
        return;
      }
    }
    setPopUpMessage("Are you sure you want to approve this request?");
    popUpSuccessMethod.current = () => onApprove?.(data, type, id, currentCreditLimit);
    setConfirmationPopUp(true);
  };

  const handleRejectClick = () => {
    setPopUpMessage("Are you sure you want to reject this request?");
    popUpSuccessMethod.current = () => onReject?.(data, type, id);
    setConfirmationPopUp(true);
  };
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, p: 0, mb: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              pt: 2,
              px: 2,
              pb: 0.5,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.5,
              fontSize: 12,
              minWidth: 0,
            }}
          >
            {/* Store name + ID */}
            <Tooltip title={`${storeName} (${storeId})`} arrow>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AccountCircleIcon sx={{ color: "text.primary", fontSize: 16, mr: 0.5, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "inherit",
                  }}
                >
                  {storeName} ({storeId})
                </Typography>
              </Box>
            </Tooltip>

            {/* Credit limit */}
            <Tooltip title={`Credit Limit: SAR ${currentCreditLimit}`} arrow>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CreditCardIcon sx={{ fontSize: 14, color: "text.secondary", mr: 0.5, flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 400, color: "text.secondary", fontSize: "inherit" }}>
                  Credit limit:{" "}
                  <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                    SAR {formatAmount(currentCreditLimit)}
                  </Box>
                </Typography>
              </Box>
            </Tooltip>

            {/* Available credit limit */}
            <Tooltip title={`Available Credit Limit: SAR ${availCreditLimit}`} arrow>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CreditCardIcon sx={{ fontSize: 14, color: "text.secondary", mr: 0.5, flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 400, color: "text.secondary", fontSize: "inherit" }}>
                  Available credit limit:{" "}
                  <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                    SAR {formatAmount(availCreditLimit)}
                  </Box>
                </Typography>
              </Box>
            </Tooltip>
          </Box>

          {type === "collection" && <Box
            sx={{
              px: 2,
              pb: 1,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.5,
              fontSize: 12,
              minWidth: 0,
            }}
          >
            {/* Invoice Number */}
            <Tooltip title={`Invoice Number: ${invoiceNumber || ""}`} arrow>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ReceiptLongIcon sx={{ fontSize: 14, color: "text.secondary", mr: 0.5, flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 400, color: "text.secondary", fontSize: "inherit" }}>
                  Invoice No:{" "}
                  <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                    {invoiceNumber || "-"}
                  </Box>
                </Typography>
              </Box>
            </Tooltip>

            {/* Order Date */}
            {/* <Tooltip title={`Order Date: ${orderDate || ""}`} arrow>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <EventIcon sx={{ fontSize: 14, color: "text.secondary", mr: 0.5, flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 400, color: "text.secondary", fontSize: "inherit" }}>
                  Order Date:{" "}
                  <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                    {orderDate || "—"}
                  </Box>
                </Typography>
              </Box>
            </Tooltip> */}
          </Box>}
        </Box>

        <Box sx={{ flexShrink: 0, alignSelf: "flex-start" }}>
          <Chip
            label={labelMap[type].text}
            sx={{
              bgcolor: labelMap[type].color + "33",
              color: labelMap[type].color,
              fontWeight: 600,
              fontSize: "12px !important",
              borderRadius: "0 !important",
              borderBottomLeftRadius: "6px !important",
              '& .MuiChip-label': { px: 1, py: 0.5 },
              height: "auto",
              minHeight: 0,
            }}
          />
        </Box>
      </Stack>


      {/* Center content */}
      <Box
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          "&::-webkit-scrollbar": {
            height: 6,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 3,
          },
        }}
      >
        <Box
          sx={{
            display: "inline-block",
            minWidth: "100%",
          }}
        >
          {renderContent()}
        </Box>
      </Box>



      {/* Action buttons */}
      <Box
        sx={{
          borderTop: "1px solid rgb(239, 239, 239)",
          backgroundColor: "white",
          p: 1,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        {status?.toLowerCase() === "approved" || status?.toLowerCase() === "rfc_approved" || status?.toLowerCase() === "rejected" || status?.toLowerCase() === "rfc_rejected" ? (
          <Stack direction="row" justifyContent="flex-end">
            <Chip
              label={(status?.toLowerCase() === "approved" || status?.toLowerCase() === "rfc_approved") ? "APPROVED" : "REJECTED"}
              sx={{
                bgcolor: "transparent",
                color: status?.toLowerCase() === "rejected" ? "#d32f2f" : "#2e7d32",
                fontWeight: 600,
                fontSize: 13,
                px: 1.5,
                py: 0.3,
                borderRadius: "6px",
                border: status?.toLowerCase() === "rejected" ? "1px solid #d32f2f" : "1px solid rgba(46, 125, 50, 0.5)", // match outlined success button
                textTransform: "none",
                height: "28px",
                display: "flex",
                alignItems: "center",
              }}
            />
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleRejectClick}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 1.5,
                py: 0.3,
                fontSize: 13,
                borderColor: "#d32f2f",
                color: "#d32f2f",
                "&:hover": {
                  borderColor: "#b71c1c",
                  backgroundColor: "transparent",
                  color: "#d32f2f",
                },
              }}
            >
              Reject
            </Button>

            <Button
              variant="outlined"
              color="success"
              size="small"
              onClick={handleApproveClick}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 1.5,
                py: 0.3,
                fontSize: 13,
                borderColor: "#2e7d32",
                color: "#2e7d32",
                "&:hover": {
                  borderColor: "#1b5e20",
                  backgroundColor: "transparent",
                  color: "#2e7d32",
                },
              }}
            >
              Approve
            </Button>
          </Stack>

        )}
        <ConfirmationPopUp
          openConfirmModal={confirmationPopUp}
          message={popUpMessage}
          setOpenConfirmModal={() => setConfirmationPopUp(false)}
          successMethod={popUpSuccessMethod.current}
        />
      </Box>

      {/* <Box
        sx={{
          borderTop: "1px solid rgb(239, 239, 239)", // subtle top border
          backgroundColor: "white", // light gray background
          p: 1, // padding inside box
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => { }}
            sx={{
              textTransform: "none", fontWeight: 600, px: 1.5, // reduce horizontal padding
              py: 0.3, // reduce vertical padding
              fontSize: 13,
            }}
          >
            Reject
          </Button>
          <Button
            variant="outlined"
            color="success"
            size="small"
            onClick={() => { }}
            sx={{
              textTransform: "none", fontWeight: 600, px: 1.5, // reduce horizontal padding
              py: 0.3, // reduce vertical padding
              fontSize: 13,
            }}
          >
            Approve
          </Button>
        </Stack>
      </Box> */}
    </Card>
  );
};

export default RequestCard;
